import React from 'react';
import s from './style.module.css';
import LazyableImage from '@Components/lazyable_image/view';

const thumbMax = 3;

export type SliderImage = {
    url: string;
};

type ImageSliderProps = {
    mainImages: SliderImage[];
    thumbnailImages: SliderImage[];
    altPrefix: string;
};

const ImageSlider: React.FC<ImageSliderProps> = (props: ImageSliderProps) => {
    const [index, setIndex] = React.useState(0);
    const rootRef = React.createRef<HTMLDivElement>();
    const thumbRootRef = React.createRef<HTMLDivElement>();

    const followParent = (index: number) => {
        if (props.mainImages.length === thumbMax) {
            return;
        }
        const thumbRoot = thumbRootRef.current;
        if (!thumbRoot) {
            return;
        }
        const coefficient = index - (thumbMax - 1) < 1 ? 0 : index - (thumbMax - 1);
        thumbRoot.style.transform = `translateX(-${coefficient * (100 / thumbMax)}%)`;
    };
    const goto = (direction: 'prev' | 'next') => {
        const root = rootRef.current;
        if (!root) {
            return;
        }
        const nextIndex =
            direction === 'next'
                ? (index + 1) % props.mainImages.length
                : (index - 1 + props.mainImages.length) % props.mainImages.length;
        root.style.transform = `translateX(-${nextIndex * root.clientWidth}px)`;
        followParent(nextIndex);
        setIndex(nextIndex);
    };

    const jump = (num: number) => {
        const root = rootRef.current;
        if (!root) {
            return;
        }
        root.style.transform = `translateX(-${num * root.clientWidth}px)`;
        setIndex(num);
    };

    return (
        <div className={s.image_slider_box}>
            <div className={s.slider_box}>
                <div className={s.slider_image_root}>
                    <div ref={rootRef} className={s.slider_root}>
                        {props.mainImages.map((i, k) => {
                            // 1枚目はページ表示直後に見えるLCP候補のため、IntersectionObserver
                            // 経由の遅延読み込み（SSR時は空のプレースホルダーになる。実測:
                            // react-lazy-load-image-component 1.6.3のisIntersectionObserverAvailable
                            // がSSR環境（windowなし）ではfalseを返すため、通常経路だとSSR HTML上に
                            // <img>が存在しない）を通さず、SSR HTMLに直接<img>として出力する。
                            // notLazyとfetchPriorityは同じ「1枚目か」の判定に紐付くため、
                            // 個別に条件を書かずisFirstを両方で共有する（分岐がズレて
                            // 片方だけ更新される事故を防ぐ）
                            const isFirst = k === 0;
                            return (
                                <div
                                    key={i.url + 'slider'}
                                    className={`${s.slider_image_box} ${s[`slider_image_box_${k}`]}`}
                                >
                                    <LazyableImage
                                        width={460}
                                        height={300}
                                        src={i.url}
                                        alt={`${props.altPrefix}の画像${k + 1}`}
                                        notLazy={isFirst}
                                        fetchPriority={isFirst ? 'high' : undefined}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
                <span className={s.control_prev} onClick={() => goto('prev')}>
                    <span className={s.icon_arrow_left} />
                </span>
                <span className={s.control_next} onClick={() => goto('next')}>
                    <span className={s.icon_arrow_right} />
                </span>
            </div>
            <div className={s.slider_thumbs}>
                <div className={s.slider_thumbs_box}>
                    <div ref={thumbRootRef} className={s.slider_thumbs_root}>
                        {props.thumbnailImages.map((i, k) => (
                            <div className={s.slider_thumbs_image} key={i.url + 'thumbs'} onClick={() => jump(k)}>
                                <LazyableImage
                                    width={150}
                                    height={105}
                                    alt={`${props.altPrefix}の画像${k + 1}`}
                                    className={`${k === index ? s.current_thumb : ''}`}
                                    src={i.url}
                                    notLazy={true}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImageSlider;
